import {
  Text,
  Flex,
  useRadioGroup,
  RadioGroup,
  Radio,
  VStack,
  Box,
  VisuallyHidden,
} from '@chakra-ui/react';
import { useCheckoutDispatch } from '@olo-web/client-state';
import { usePaymentMethods } from '@olo-web/components/templates/Checkout/common/hooks/usePaymentMethods';
import { EPaymentMethods } from '@olo-web/types/enums/paymentMethods.enum';
import { PaymentMethodRadioCard } from './PaymentMethodRadioCard.ui';
import { getPaymentMethodAriaLabel } from './getPaymentMethodAriaLabel';
import {
  useIsDineIn,
  useHasStartedTab,
  useIsStartATabModal,
  useSendEvent,
} from '@olo-web/utils/common/hooks';
import { EAnalyticsEventNames } from '@olo-web/types/enums';

export const SelectedPaymentMethod = (): JSX.Element | null => {
  const checkoutDispatch = useCheckoutDispatch();
  const { paymentMethods, selectedPaymentMethod, isCreditCardOnly } = usePaymentMethods();
  const isDineIn = useIsDineIn();
  const { sendEvent } = useSendEvent();
  const isStartATabModal = useIsStartATabModal();
  const isStartATabAuth = useHasStartedTab();

  const { getRadioProps } = useRadioGroup({
    value: selectedPaymentMethod,
    name: 'paymentMethod',
    onChange: (value: EPaymentMethods) => {
      if (isStartATabAuth) {
        checkoutDispatch({
          type: 'REMOVE_PAYMENT',
        });
        checkoutDispatch({
          type: 'SET_START_A_TAB_PAYMENT_TYPE',
          payload: value,
        });
      }
      sendEvent(EAnalyticsEventNames.SWITCH_PAYMENT_METHOD, {
        googleAnalytics: {
          paymentType: value,
        },
      });
      checkoutDispatch({
        type: 'SELECT_PAYMENT_METHOD',
        payload: value,
      });
    },
  });

  const filteredPaymentMethods =
    isDineIn || isStartATabModal
      ? paymentMethods.filter((paymentMethod) => paymentMethod?.title !== 'Cash')
      : paymentMethods;

  if (isCreditCardOnly) return null;

  return (
    <VStack
      direction="column"
      spacing={4}
      alignItems="flex-start"
      justifyContent="flex-start"
      w="100%"
      backgroundColor="white"
    >
      <Box as="fieldset" border="none" p={0} m={0} w="100%">
        <VisuallyHidden as="legend">Payment method</VisuallyHidden>
        <RadioGroup w="100%">
          {filteredPaymentMethods.map((paymentMethod, i) => {
            const radio = getRadioProps({
              value: paymentMethod.value,
            });
            return (
              <PaymentMethodRadioCard
                borderTopRadius={i === 0 ? 'md' : 'none'}
                borderBottomRadius={i === filteredPaymentMethods.length - 1 ? 'md' : 'none'}
                key={paymentMethod?.title}
                value={radio.value || ''}
                {...radio}
              >
                <Radio
                  //@ts-ignore
                  isChecked={radio.isChecked}
                  onChange={() => {
                    radio.onChange(radio.value);
                  }}
                  inputProps={{ 'aria-label': getPaymentMethodAriaLabel(paymentMethod) }}
                  data-testid={`${paymentMethod.title}-radio-button`}
                  w="100%"
                >
                  <Flex align="center" justifyContent="space-between">
                    <Text ml={4} textStyle={radio.isChecked ? 'pBold' : 'p'}>
                      {paymentMethod?.title}
                    </Text>
                    {paymentMethod.icon}
                  </Flex>
                </Radio>
              </PaymentMethodRadioCard>
            );
          })}
        </RadioGroup>
      </Box>
    </VStack>
  );
};
